/**
 * FormulaBuilder Component
 *
 * Visual formula editor for settlement rules. Allows users to build
 * arithmetic expressions using variables from snapshot tables and market data.
 *
 * Outputs a formula JSON structure:
 * {
 *   type: "expression" | "percentage" | "fixed",
 *   expression: "var1 * var2 + const",
 *   variables: {
 *     var1: { source: "group_snapshot", field: "total_headroom_kw" },
 *     var2: { source: "market", field: "fcas_price" }
 *   },
 *   constants: { rate: 0.8 }
 * }
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  Tooltip,
  Divider,
  Alert,
  theme,
  Modal,
  Form,
  Steps,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  DatabaseOutlined,
  DollarOutlined,
  FunctionOutlined,
  EditOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

// Variable sources and their available fields
// Naming convention:
//   - _day suffix: daily total/average value (single number)
//   - _288 suffix: 288-point array (one value per 5-min interval, settled per interval)
//   - No suffix for static values (counts, capacities, rates)
const VARIABLE_SOURCES = {
  GROUP_REVENUE: {
    group_snapshot: {
      label: "Control Group Snapshot",
      icon: <DatabaseOutlined />,
      color: "blue",
      fields: [
        // Static / Daily counts
        { value: "system_count", label: "System Count", description: "Number of systems in group" },
        { value: "online_count_day", label: "Online Count (avg)", description: "Average online systems for the day" },
        { value: "activated_count", label: "Activated Count", description: "Number of activated systems" },
        { value: "inverter_kw", label: "Total Inverter (kW)", description: "Total inverter capacity" },
        { value: "battery_kwh", label: "Total Battery (kWh)", description: "Total battery capacity" },
        // Daily totals (_day suffix)
        { value: "headroom_day", label: "Headroom Day (kW)", description: "Daily average headroom capacity" },
        { value: "footroom_day", label: "Footroom Day (kW)", description: "Daily average footroom capacity" },
        { value: "pv_day", label: "PV Day (kWh)", description: "Total PV generation for the day" },
        { value: "load_day", label: "Load Day (kWh)", description: "Total load consumption for the day" },
        { value: "grid_import_day", label: "Grid Import Day (kWh)", description: "Energy imported from grid" },
        { value: "grid_export_day", label: "Grid Export Day (kWh)", description: "Energy exported to grid" },
        // 288-point interval arrays (_288 suffix)
        { value: "headroom_288", label: "Headroom [288]", description: "Headroom per 5-min interval (kW)", isArray: true },
        { value: "footroom_288", label: "Footroom [288]", description: "Footroom per 5-min interval (kW)", isArray: true },
        { value: "pv_288", label: "PV Power [288]", description: "PV generation per interval (kW)", isArray: true },
        { value: "load_288", label: "Load Power [288]", description: "Load per interval (kW)", isArray: true },
        { value: "grid_288", label: "Grid Power [288]", description: "Grid power per interval (kW)", isArray: true },
        { value: "battery_288", label: "Battery Power [288]", description: "Battery power per interval (kW)", isArray: true },
        { value: "online_288", label: "Online Count [288]", description: "Online systems count per interval", isArray: true },
      ],
    },
    market: {
      label: "AEMO Market Price",
      icon: <DollarOutlined />,
      color: "green",
      fields: [
        // Daily average prices (_day suffix)
        { value: "energy_price_day", label: "Energy Price Day ($/MWh)", description: "Daily average spot energy price" },
        { value: "raise_6sec_price_day", label: "Raise 6sec Day ($/MW)", description: "Daily avg Raise 6sec price" },
        { value: "raise_60sec_price_day", label: "Raise 60sec Day ($/MW)", description: "Daily avg Raise 60sec price" },
        { value: "raise_5min_price_day", label: "Raise 5min Day ($/MW)", description: "Daily avg Raise 5min price" },
        { value: "raise_reg_price_day", label: "Raise Reg Day ($/MW)", description: "Daily avg Raise regulation price" },
        { value: "lower_6sec_price_day", label: "Lower 6sec Day ($/MW)", description: "Daily avg Lower 6sec price" },
        { value: "lower_60sec_price_day", label: "Lower 60sec Day ($/MW)", description: "Daily avg Lower 60sec price" },
        { value: "lower_5min_price_day", label: "Lower 5min Day ($/MW)", description: "Daily avg Lower 5min price" },
        { value: "lower_reg_price_day", label: "Lower Reg Day ($/MW)", description: "Daily avg Lower regulation price" },
        // 288-point interval prices (_288 suffix)
        { value: "energy_price_288", label: "Energy Price [288]", description: "Spot price per interval ($/MWh)", isArray: true },
        { value: "raise_6sec_price_288", label: "Raise 6sec [288]", description: "Raise 6sec price per interval ($/MW)", isArray: true },
        { value: "raise_60sec_price_288", label: "Raise 60sec [288]", description: "Raise 60sec price per interval ($/MW)", isArray: true },
        { value: "raise_5min_price_288", label: "Raise 5min [288]", description: "Raise 5min price per interval ($/MW)", isArray: true },
        { value: "raise_reg_price_288", label: "Raise Reg [288]", description: "Raise regulation price per interval ($/MW)", isArray: true },
        { value: "lower_6sec_price_288", label: "Lower 6sec [288]", description: "Lower 6sec price per interval ($/MW)", isArray: true },
        { value: "lower_60sec_price_288", label: "Lower 60sec [288]", description: "Lower 60sec price per interval ($/MW)", isArray: true },
        { value: "lower_5min_price_288", label: "Lower 5min [288]", description: "Lower 5min price per interval ($/MW)", isArray: true },
        { value: "lower_reg_price_288", label: "Lower Reg [288]", description: "Lower regulation price per interval ($/MW)", isArray: true },
      ],
    },
    aemo_settlement: {
      label: "AEMO Settlement (DUID)",
      icon: <DollarOutlined />,
      color: "gold",
      fields: [
        // Daily total payments (_day suffix)
        { value: "total_payment_day", label: "Total Payment Day ($)", description: "Total FCAS payment for the day" },
        { value: "raise_6sec_payment_day", label: "Raise 6sec Day ($)", description: "Raise 6sec payment for the day" },
        { value: "raise_60sec_payment_day", label: "Raise 60sec Day ($)", description: "Raise 60sec payment for the day" },
        { value: "raise_5min_payment_day", label: "Raise 5min Day ($)", description: "Raise 5min payment for the day" },
        { value: "raise_reg_payment_day", label: "Raise Reg Day ($)", description: "Raise regulation payment for the day" },
        { value: "lower_6sec_payment_day", label: "Lower 6sec Day ($)", description: "Lower 6sec payment for the day" },
        { value: "lower_60sec_payment_day", label: "Lower 60sec Day ($)", description: "Lower 60sec payment for the day" },
        { value: "lower_5min_payment_day", label: "Lower 5min Day ($)", description: "Lower 5min payment for the day" },
        { value: "lower_reg_payment_day", label: "Lower Reg Day ($)", description: "Lower regulation payment for the day" },
        // 288-point interval payments (_288 suffix)
        { value: "total_payment_288", label: "Total Payment [288]", description: "Total FCAS payment per interval ($)", isArray: true },
        { value: "raise_6sec_payment_288", label: "Raise 6sec [288]", description: "Raise 6sec payment per interval ($)", isArray: true },
        { value: "raise_60sec_payment_288", label: "Raise 60sec [288]", description: "Raise 60sec payment per interval ($)", isArray: true },
        { value: "raise_5min_payment_288", label: "Raise 5min [288]", description: "Raise 5min payment per interval ($)", isArray: true },
        { value: "raise_reg_payment_288", label: "Raise Reg [288]", description: "Raise regulation payment per interval ($)", isArray: true },
        { value: "lower_6sec_payment_288", label: "Lower 6sec [288]", description: "Lower 6sec payment per interval ($)", isArray: true },
        { value: "lower_60sec_payment_288", label: "Lower 60sec [288]", description: "Lower 60sec payment per interval ($)", isArray: true },
        { value: "lower_5min_payment_288", label: "Lower 5min [288]", description: "Lower 5min payment per interval ($)", isArray: true },
        { value: "lower_reg_payment_288", label: "Lower Reg [288]", description: "Lower regulation payment per interval ($)", isArray: true },
      ],
    },
    dispatch: {
      label: "Dispatch Data",
      icon: <FunctionOutlined />,
      color: "purple",
      fields: [
        // Daily metrics (_day suffix)
        { value: "dispatch_energy_day", label: "Dispatch Energy Day (kWh)", description: "Total energy dispatched" },
        { value: "dispatch_intervals", label: "Dispatch Intervals", description: "Number of dispatch intervals" },
        { value: "completion_rate", label: "Completion Rate (%)", description: "Dispatch completion rate" },
        { value: "performance_score", label: "Performance Score", description: "Dispatch performance score" },
        // 288-point array (_288 suffix)
        { value: "dispatch_288", label: "Dispatch Power [288]", description: "Dispatched power per interval (kW)", isArray: true },
      ],
    },
  },
  SYSTEM_ALLOCATION: {
    group_settlement: {
      label: "Group Settlement",
      icon: <DollarOutlined />,
      color: "green",
      fields: [
        // Daily totals (_day suffix)
        { value: "revenue_day", label: "Group Revenue Day ($)", description: "Total revenue to allocate" },
        { value: "fcas_revenue_day", label: "FCAS Revenue Day ($)", description: "FCAS portion of revenue" },
        { value: "energy_revenue_day", label: "Energy Revenue Day ($)", description: "Energy portion of revenue" },
        // 288-point array (_288 suffix)
        { value: "revenue_288", label: "Group Revenue [288]", description: "Revenue per 5-min interval ($)", isArray: true },
      ],
    },
    group_snapshot: {
      label: "Group Snapshot",
      icon: <DatabaseOutlined />,
      color: "blue",
      fields: [
        // Static / counts
        { value: "system_count", label: "System Count", description: "Number of systems in group" },
        // Daily totals (_day suffix)
        { value: "headroom_day", label: "Group Headroom Day (kW)", description: "Group daily headroom" },
        { value: "pv_day", label: "Group PV Day (kWh)", description: "Group total PV for the day" },
        // 288-point array (_288 suffix)
        { value: "headroom_288", label: "Group Headroom [288]", description: "Group headroom per interval (kW)", isArray: true },
        { value: "online_288", label: "Group Online [288]", description: "Group online count per interval", isArray: true },
      ],
    },
    system_snapshot: {
      label: "System Snapshot",
      icon: <DatabaseOutlined />,
      color: "cyan",
      fields: [
        // Static capacities
        { value: "inverter_kw", label: "Inverter Capacity (kW)", description: "System inverter capacity" },
        { value: "battery_kwh", label: "Battery Capacity (kWh)", description: "System battery capacity" },
        // Daily totals (_day suffix)
        { value: "headroom_day", label: "System Headroom Day (kW)", description: "System daily headroom" },
        { value: "pv_day", label: "System PV Day (kWh)", description: "System PV for the day" },
        { value: "load_day", label: "System Load Day (kWh)", description: "System load for the day" },
        { value: "online_pct", label: "Online Percentage (%)", description: "System online percentage for the day" },
        // 288-point arrays (_288 suffix)
        { value: "headroom_288", label: "System Headroom [288]", description: "System headroom per interval (kW)", isArray: true },
        { value: "pv_288", label: "System PV [288]", description: "System PV power per interval (kW)", isArray: true },
        { value: "load_288", label: "System Load [288]", description: "System load per interval (kW)", isArray: true },
        { value: "online_288", label: "System Online [288]", description: "Online status per interval (0/1)", isArray: true },
      ],
    },
  },
  COMPANY_REVENUE: {
    system_allocation: {
      label: "System Allocations",
      icon: <DollarOutlined />,
      color: "green",
      fields: [
        // Aggregated from all company systems (_day suffix)
        { value: "sum_revenue_day", label: "Sum Revenue Day ($)", description: "Total revenue from all systems" },
        { value: "sum_energy_day", label: "Sum Energy Day (kWh)", description: "Total energy from all systems" },
        // 288-point arrays (_288 suffix)
        { value: "sum_revenue_288", label: "Sum Revenue [288]", description: "Sum revenue per interval ($)", isArray: true },
      ],
    },
    company_data: {
      label: "Company Data",
      icon: <DatabaseOutlined />,
      color: "blue",
      fields: [
        // Static counts
        { value: "system_count", label: "Company System Count", description: "Number of company systems" },
        { value: "total_capacity_kw", label: "Total Capacity (kW)", description: "Company total capacity" },
      ],
    },
  },
  CUSTOMER_PRICING: {
    system_snapshot: {
      label: "System Snapshot",
      icon: <DatabaseOutlined />,
      color: "cyan",
      fields: [
        // Static capacities
        { value: "inverter_kw", label: "Inverter Capacity (kW)", description: "System inverter capacity" },
        { value: "battery_kwh", label: "Battery Capacity (kWh)", description: "System battery capacity" },
        // Daily totals (_day suffix)
        { value: "revenue_day", label: "System Revenue Day ($)", description: "Revenue allocated to system for the day" },
        { value: "pv_day", label: "System PV Day (kWh)", description: "System PV generation for the day" },
        { value: "load_day", label: "System Load Day (kWh)", description: "System load consumption for the day" },
        { value: "export_day", label: "System Export Day (kWh)", description: "System grid export for the day" },
        { value: "import_day", label: "System Import Day (kWh)", description: "System grid import for the day" },
        { value: "online_pct", label: "Online Percentage (%)", description: "System online percentage for the day" },
        // 288-point arrays (_288 suffix)
        { value: "revenue_288", label: "System Revenue [288]", description: "Revenue per interval ($)", isArray: true },
        { value: "pv_288", label: "System PV [288]", description: "PV power per interval (kW)", isArray: true },
        { value: "load_288", label: "System Load [288]", description: "Load per interval (kW)", isArray: true },
        { value: "online_288", label: "System Online [288]", description: "Online status per interval (0/1)", isArray: true },
      ],
    },
    pricing_data: {
      label: "Pricing Data",
      icon: <DollarOutlined />,
      color: "green",
      fields: [
        // Static rates (no suffix)
        { value: "base_rate", label: "Base Rate ($/kWh)", description: "Base pricing rate" },
        { value: "bonus_rate", label: "Bonus Rate ($/kWh)", description: "Bonus pricing rate" },
        { value: "penalty_rate", label: "Penalty Rate ($/kWh)", description: "Penalty pricing rate" },
      ],
    },
  },
};

// Operators for formula building
const OPERATORS = [
  { value: "+", label: "+", description: "Add" },
  { value: "-", label: "-", description: "Subtract" },
  { value: "*", label: "×", description: "Multiply" },
  { value: "/", label: "÷", description: "Divide" },
  { value: "(", label: "(", description: "Open parenthesis" },
  { value: ")", label: ")", description: "Close parenthesis" },
];

// Default templates for each rule type
const DEFAULT_TEMPLATES = {
  GROUP_REVENUE: {
    type: "expression",
    expression: "(headroom_day * raise_reg_price_day * rate) / 1000",
    variables: {
      headroom_day: { source: "group_snapshot", field: "headroom_day" },
      raise_reg_price_day: { source: "market", field: "raise_reg_price_day" },
    },
    constants: {
      rate: { type: "number", value: 0.8, min: 0, max: 1, description: "Revenue share rate" },
    },
  },
  SYSTEM_ALLOCATION: {
    type: "expression",
    expression: "revenue_day * (sys_headroom_day / grp_headroom_day)",
    variables: {
      revenue_day: { source: "group_settlement", field: "revenue_day" },
      sys_headroom_day: { source: "system_snapshot", field: "headroom_day" },
      grp_headroom_day: { source: "group_snapshot", field: "headroom_day" },
    },
    constants: {},
  },
  COMPANY_REVENUE: {
    type: "expression",
    expression: "sum_revenue_day * company_rate",
    variables: {
      sum_revenue_day: { source: "system_allocation", field: "sum_revenue_day" },
    },
    constants: {
      company_rate: { type: "number", value: 0.7, min: 0, max: 1, description: "Company revenue share" },
    },
  },
  CUSTOMER_PRICING: {
    type: "expression",
    expression: "revenue_day * customer_rate + (pv_day * bonus_rate)",
    variables: {
      revenue_day: { source: "system_snapshot", field: "revenue_day" },
      pv_day: { source: "system_snapshot", field: "pv_day" },
      bonus_rate: { source: "pricing_data", field: "bonus_rate" },
    },
    constants: {
      customer_rate: { type: "number", value: 0.85, min: 0, max: 1, description: "Customer revenue share" },
    },
  },
};

export default function FormulaBuilder({ ruleType, value, onChange, variables = [] }) {
  const { token } = theme.useToken();
  const [formula, setFormula] = useState(value || DEFAULT_TEMPLATES[ruleType] || {});
  const [expressionParts, setExpressionParts] = useState([]);

  // Variable search state
  const [variableSearchText, setVariableSearchText] = useState("");

  // Constant modal state
  const [constantModalVisible, setConstantModalVisible] = useState(false);
  const [constantForm] = Form.useForm();

  // Get available sources for this rule type (for backward compatibility)
  const availableSources = VARIABLE_SOURCES[ruleType] || {};

  // Filter variables for this rule type from database
  const availableVariables = variables.filter(v => v.rule_type === ruleType && v.is_active === 'YES');

  // Filter variables by search text
  const filteredVariables = availableVariables.filter(v => {
    if (!variableSearchText) return true;
    const searchLower = variableSearchText.toLowerCase();
    return (
      v.variable_name.toLowerCase().includes(searchLower) ||
      v.display_name.toLowerCase().includes(searchLower) ||
      v.column_name.toLowerCase().includes(searchLower) ||
      v.table_name.toLowerCase().includes(searchLower) ||
      (v.description && v.description.toLowerCase().includes(searchLower))
    );
  });

  // Parse expression into parts for display
  useEffect(() => {
    if (formula.expression) {
      // Split expression keeping operators
      const parts = formula.expression.split(/([+\-*/()])/g).filter(p => p.trim());
      setExpressionParts(parts);
    }
  }, [formula.expression]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(formula);
  }, [formula, onChange]);

  // Add variable to formula
  const addVariable = (name, variableId, variableMetadata) => {
    setFormula(prev => ({
      ...prev,
      variables: {
        ...(prev.variables || {}),
        [name]: {
          variable_id: variableId,
          data_source: variableMetadata.data_source,
          table_name: variableMetadata.table_name,
          column_name: variableMetadata.column_name,
          display_name: variableMetadata.display_name,
          is_array: variableMetadata.is_array || false,
        },
      },
    }));
  };

  // Remove variable
  const removeVariable = (name) => {
    setFormula(prev => {
      const { [name]: removed, ...rest } = prev.variables || {};
      return { ...prev, variables: rest };
    });
  };

  // Add constant
  const addConstant = (name, value = 1, description = "", min = 0, max = 100) => {
    setFormula(prev => ({
      ...prev,
      constants: {
        ...(prev.constants || {}),
        [name]: { type: "number", value, min, max, description },
      },
    }));
  };

  // Update constant
  const updateConstant = (name, field, value) => {
    setFormula(prev => ({
      ...prev,
      constants: {
        ...(prev.constants || {}),
        [name]: { ...(prev.constants?.[name] || {}), [field]: value },
      },
    }));
  };

  // Remove constant
  const removeConstant = (name) => {
    setFormula(prev => {
      const { [name]: removed, ...rest } = prev.constants || {};
      return { ...prev, constants: rest };
    });
  };

  // Update expression
  const updateExpression = (expr) => {
    setFormula(prev => ({ ...prev, expression: expr }));
  };

  // Insert into expression at cursor position
  const insertIntoExpression = (text) => {
    setFormula(prev => ({
      ...prev,
      expression: (prev.expression || "") + " " + text,
    }));
  };

  // Load default template
  const loadTemplate = () => {
    const template = DEFAULT_TEMPLATES[ruleType];
    if (template) {
      setFormula(template);
    }
  };

  // Validate expression
  const validateExpression = () => {
    const expr = formula.expression || "";
    const variables = Object.keys(formula.variables || {});
    const constants = Object.keys(formula.constants || {});
    const allNames = [...variables, ...constants];

    // Check if all used names are defined
    const usedNames = expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
    const undefinedNames = usedNames.filter(n => !allNames.includes(n));

    if (undefinedNames.length > 0) {
      return { valid: false, error: `Undefined variables: ${undefinedNames.join(", ")}` };
    }

    // Check parentheses balance
    let balance = 0;
    for (const char of expr) {
      if (char === "(") balance++;
      if (char === ")") balance--;
      if (balance < 0) return { valid: false, error: "Unbalanced parentheses" };
    }
    if (balance !== 0) return { valid: false, error: "Unbalanced parentheses" };

    return { valid: true };
  };

  const validation = validateExpression();

  return (
    <div>
      {/* Expression Editor */}
      <Card
        size="small"
        title={
          <Space>
            <CalculatorOutlined style={{ color: token.colorPrimary }} />
            <Text strong>Formula Expression</Text>
          </Space>
        }
        extra={
          <Button type="primary" size="small" onClick={loadTemplate}>
            Load Template
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Input.TextArea
          value={formula.expression || ""}
          onChange={(e) => updateExpression(e.target.value)}
          placeholder="e.g., (headroom * fcas_price * rate) / 1000"
          rows={2}
          style={{ fontFamily: "monospace", marginBottom: 12 }}
        />

        {!validation.valid && (
          <Alert message={validation.error} type="error" showIcon style={{ marginBottom: 12 }} />
        )}

        {/* Quick insert buttons */}
        <Space wrap size={4}>
          <Text type="secondary" style={{ fontSize: 12 }}>Insert:</Text>
          {OPERATORS.map(op => (
            <Tooltip key={op.value} title={op.description}>
              <Button
                size="small"
                onClick={() => insertIntoExpression(op.value)}
              >
                {op.label}
              </Button>
            </Tooltip>
          ))}
        </Space>
      </Card>

      {/* Variables Panel - Two Columns */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {/* Used Variables - Left Column */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <DatabaseOutlined style={{ color: token.colorInfo }} />
                <Text strong>Variables in Formula</Text>
                <Tag color="blue">{Object.keys(formula.variables || {}).length}</Tag>
              </Space>
            }
            style={{ height: '100%', minHeight: 300 }}
          >
            {Object.keys(formula.variables || {}).length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: token.colorTextSecondary }}>
                <DatabaseOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }} />
                <div>No variables in formula yet</div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  Click variables from the right panel to add them
                </Text>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {Object.entries(formula.variables || {}).map(([name, config]) => {
                    // Check if this is new format (with variable_id) or old format (with source/field)
                    const isNewFormat = config.variable_id;
                    const dataSourceColor = config.data_source === 'MYSQL' ? 'blue' :
                                           config.data_source === 'CASSANDRA' ? 'purple' :
                                           config.data_source === 'COMPUTED' ? 'orange' :
                                           config.data_source === 'PLATFORM_STATISTICS' ? 'cyan' : 'default';

                    // For old format, try to get sourceConfig
                    const sourceConfig = !isNewFormat && config.source ? availableSources[config.source] : null;
                    const fieldConfig = !isNewFormat && sourceConfig ? sourceConfig?.fields?.find(f => f.value === config.field) : null;
                    const isArrayField = config.is_array || config.field?.endsWith("_288") || fieldConfig?.isArray;

                    return (
                      <div
                        key={name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          background: token.colorBgLayout,
                          borderRadius: 6,
                          border: `1px solid ${isArrayField ? token.colorPurple : token.colorBorderSecondary}`,
                        }}
                      >
                        <Space size="small" style={{ flex: 1 }}>
                          <Tag
                            color={isNewFormat ? dataSourceColor : (isArrayField ? "purple" : "blue")}
                            style={{ cursor: "pointer", fontFamily: "monospace", minWidth: 100 }}
                            onClick={() => insertIntoExpression(name)}
                          >
                            {name}
                          </Tag>
                          {isArrayField && (
                            <Tooltip title="This variable uses 288-point interval data. Settlement will be calculated per 5-minute interval.">
                              <Tag color="purple" style={{ fontSize: 10 }}>Per Interval (288)</Tag>
                            </Tooltip>
                          )}
                          {isNewFormat ? (
                            <div>
                              <Text style={{ fontSize: 11, display: 'block' }}>{config.display_name || name}</Text>
                              <Text type="secondary" style={{ fontSize: 10 }}>
                                {config.table_name}.{config.column_name}
                              </Text>
                            </div>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {sourceConfig?.label || config.source} → {fieldConfig?.label || config.field}
                            </Text>
                          )}
                        </Space>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeVariable(name)}
                        />
                      </div>
                    );
                  })}
                </Space>
              </div>
            )}
          </Card>
        </Col>

        {/* Available Variables - Right Column */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <AppstoreOutlined style={{ color: token.colorSuccess }} />
                <Text strong>Available Variables</Text>
                <Tag color="green">{filteredVariables.length}</Tag>
              </Space>
            }
            extra={
              <Input.Search
                placeholder="Search variables or columns..."
                value={variableSearchText}
                onChange={(e) => setVariableSearchText(e.target.value)}
                style={{ width: 200 }}
                allowClear
                size="small"
              />
            }
            style={{ height: '100%', minHeight: 300 }}
          >
            {filteredVariables.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: token.colorTextSecondary }}>
                <DatabaseOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }} />
                <div>No variables available for {ruleType}</div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                  Add variables in Variable Management tab
                </Text>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {filteredVariables.map((variable) => {
                    const dataSourceColor = variable.data_source === 'MYSQL' ? 'blue' :
                                          variable.data_source === 'CASSANDRA' ? 'purple' :
                                          variable.data_source === 'COMPUTED' ? 'orange' :
                                          variable.data_source === 'PLATFORM_STATISTICS' ? 'cyan' : 'default';
                    const isAlreadyAdded = Object.values(formula.variables || {}).some(
                      v => v.variable_id === variable.variable_id
                    );

                    return (
                      <div
                        key={variable.variable_id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 12px",
                          background: isAlreadyAdded ? token.colorBgContainerDisabled : token.colorBgLayout,
                          borderRadius: 6,
                          border: `1px solid ${token.colorBorder}`,
                          opacity: isAlreadyAdded ? 0.5 : 1,
                          cursor: isAlreadyAdded ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => {
                          if (!isAlreadyAdded) {
                            addVariable(variable.variable_name, variable.variable_id, variable);
                          }
                        }}
                      >
                        <Space size="small" style={{ flex: 1 }}>
                          <Tag color={dataSourceColor} style={{ fontSize: 10, minWidth: 70 }}>
                            {variable.data_source === "MYSQL" ? "PostgreSQL" : variable.data_source === "COMPUTED" ? "Computed" : variable.data_source}
                          </Tag>
                          <div style={{ flex: 1 }}>
                            <div>
                              <Text strong style={{ fontSize: 12 }}>{variable.display_name}</Text>
                              {isAlreadyAdded && <Tag color="default" style={{ marginLeft: 4, fontSize: 10 }}>Added</Tag>}
                              {variable.is_array && <Tag color="cyan" style={{ marginLeft: 4, fontSize: 10 }}>288 Points</Tag>}
                            </div>
                            <Text code type="secondary" style={{ fontSize: 10, display: 'block' }}>
                              {variable.variable_name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                              {variable.table_name}.{variable.column_name}
                            </Text>
                            {variable.description && (
                              <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>
                                {variable.description}
                              </Text>
                            )}
                          </div>
                        </Space>
                        {!isAlreadyAdded && (
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              addVariable(variable.variable_name, variable.variable_id, variable);
                            }}
                          >
                            Add
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </Space>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Constants Panel - Full Width */}
      <Card
        size="small"
        title={
          <Space>
            <FunctionOutlined style={{ color: token.colorWarning }} />
            <Text strong>Constants</Text>
            <Tag color="orange">{Object.keys(formula.constants || {}).length}</Tag>
          </Space>
        }
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setConstantModalVisible(true)}
          >
            Add Constant
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {Object.keys(formula.constants || {}).length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: token.colorTextSecondary }}>
            <FunctionOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }} />
            <div>No constants defined yet</div>
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setConstantModalVisible(true)}
            >
              Click here to add your first constant
            </Button>
          </div>
        ) : (
          <Row gutter={[12, 12]}>
            {Object.entries(formula.constants || {}).map(([name, config]) => (
              <Col key={name} xs={24}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    background: token.colorBgLayout,
                    borderRadius: 6,
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <Space size="large" style={{ flex: 1 }}>
                    <Tag
                      color="orange"
                      style={{ cursor: "pointer", fontFamily: "monospace", minWidth: 80 }}
                      onClick={() => insertIntoExpression(name)}
                    >
                      {name}
                    </Tag>
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>Value:</Text>
                      <InputNumber
                        size="small"
                        value={config.value}
                        onChange={(v) => updateConstant(name, "value", v)}
                        controls={false}
                        precision={2}
                        style={{ width: 100 }}
                      />
                    </Space>
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>Min:</Text>
                      <InputNumber
                        size="small"
                        value={config.min}
                        onChange={(v) => updateConstant(name, "min", v)}
                        controls={false}
                        precision={2}
                        style={{ width: 80 }}
                      />
                    </Space>
                    <Space size="small">
                      <Text type="secondary" style={{ fontSize: 12 }}>Max:</Text>
                      <InputNumber
                        size="small"
                        value={config.max}
                        onChange={(v) => updateConstant(name, "max", v)}
                        controls={false}
                        precision={2}
                        style={{ width: 80 }}
                      />
                    </Space>
                    {config.description && (
                      <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                        {config.description}
                      </Text>
                    )}
                  </Space>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeConstant(name)}
                  />
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Preview */}
      <Card size="small" title="Formula Preview (JSON)">
        <pre
          style={{
            background: token.colorBgLayout,
            padding: 12,
            borderRadius: 4,
            fontSize: 11,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {JSON.stringify(formula, null, 2)}
        </pre>
      </Card>

      {/* Add Constant Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <span>Add Constant</span>
          </Space>
        }
        open={constantModalVisible}
        onCancel={() => {
          setConstantModalVisible(false);
          constantForm.resetFields();
        }}
        onOk={() => {
          constantForm.validateFields().then(values => {
            addConstant(values.name, values.value, values.description || "", values.min ?? 0, values.max ?? 100);
            constantForm.resetFields();
            setConstantModalVisible(false);
          });
        }}
        okText="Add Constant"
        width={400}
      >
        <Form
          form={constantForm}
          layout="vertical"
          style={{ marginTop: 16 }}
          initialValues={{ value: 1, min: 0, max: 100 }}
        >
          <Form.Item
            name="name"
            label="Constant Name"
            rules={[
              { required: true, message: "Please enter a constant name" },
              { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: "Use letters, numbers, and underscores only" },
            ]}
            extra="This name will be used in the formula expression"
          >
            <Input
              placeholder="e.g., rate, threshold, multiplier"
              style={{ fontFamily: "monospace" }}
            />
          </Form.Item>

          <Form.Item
            name="value"
            label="Default Value"
            rules={[{ required: true, message: "Please enter a value" }]}
          >
            <InputNumber
              placeholder="1.0"
              controls={false}
              precision={2}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min"
                label="Min Value"
              >
                <InputNumber
                  placeholder="0"
                  controls={false}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max"
                label="Max Value"
              >
                <InputNumber
                  placeholder="100"
                  controls={false}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description (optional)"
          >
            <Input placeholder="Brief description of this constant" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
